"use client";

import { useEffect, type FC } from "react";
import type { Inquiry } from "cmw-client";
import { Tag } from "@/components/tag";
import { InquiryNewBadge } from "../inquiry-new-badge";
import { useMarkInquiryRead } from "../../hooks/use-inquiries";

const DETAILS_DATE_FORMAT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("") || "?";

type DetailFieldProps = { label: string; value: string };

const DetailField: FC<DetailFieldProps> = ({ label, value }) => (
  <div className="flex min-w-[140px] flex-1 flex-col gap-12 text-16 leading-[1.7]">
    <p className="text-neutral-black/50">{label}</p>
    <p className="text-neutral-black">{value}</p>
  </div>
);

type InquiryDetailProps = {
  inquiry: Inquiry;
};

/**
 * The "Message" drawer panel (Figma 2:13387), opened from the Action column's "See Message"
 * button. Flips the inquiry to "read" the moment it opens — the practitioner is looking at it
 * either way, so there is no separate "mark as read" affordance to hunt for.
 */
export const InquiryDetail: FC<InquiryDetailProps> = ({ inquiry }) => {
  const markRead = useMarkInquiryRead();

  useEffect(() => {
    if (inquiry.status === "new") markRead.mutate(inquiry.id);
    /* Only ever meant to fire once, for the inquiry this panel was opened with. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiry.id]);

  return (
    <div className="flex flex-col gap-24">
      <div className="flex items-center justify-between gap-24 rounded-medium border border-tone-ice bg-brand-blue/5 px-24 py-32">
        <div className="flex min-w-0 items-center gap-12">
          <div className="flex size-52 shrink-0 items-center justify-center rounded-circle bg-tone-ice text-16 font-bold text-neutral-black">
            {getInitials(inquiry.fullName)}
          </div>
          <p className="truncate text-18 leading-[1.4] font-medium text-neutral-black">{inquiry.fullName}</p>
        </div>
        {inquiry.status === "new" && <InquiryNewBadge />}
      </div>

      <div className="flex flex-col gap-24 rounded-medium border border-tone-ice bg-brand-blue/5 px-24 py-32">
        <div className="flex items-center justify-between text-neutral-black">
          <p className="text-[21px] leading-[1.2]">Details</p>
          <p className="text-16 leading-[1.7]">{DETAILS_DATE_FORMAT.format(new Date(inquiry.createdAt))}</p>
        </div>

        <div className="flex flex-wrap gap-24">
          <DetailField label="Name" value={inquiry.fullName} />
          <DetailField label="Email" value={inquiry.email} />
        </div>
        <div className="flex flex-wrap gap-24">
          <DetailField label="Phone" value={inquiry.phone} />
          {/* Figma shows "Preferred Timeframe" here; the form collects a session type instead —
              same relabelling `InquiryReceivedEmail` already made. */}
          <DetailField label="Preferred Session Type" value={inquiry.sessionType?.label ?? "No preference"} />
        </div>

        <div className="flex flex-col gap-12">
          <p className="text-16 leading-[1.7] text-neutral-black/50">Issues</p>
          <div className="flex flex-wrap gap-12">
            {inquiry.issues.map((issue) => (
              <Tag key={issue.slug} size="m" iconRight={false}>
                {issue.label}
              </Tag>
            ))}
          </div>
        </div>

        {inquiry.comment && (
          <>
            <hr className="border-neutral-mid-grey" />
            <div className="flex flex-col gap-4">
              <p className="text-16 leading-[1.4] font-medium text-neutral-black/50">Message</p>
              <p className="text-16 leading-[1.7] whitespace-pre-wrap text-neutral-black">{inquiry.comment}</p>
            </div>
          </>
        )}
        {!inquiry.comment && inquiry.commentRedactedAt && (
          <>
            <hr className="border-neutral-mid-grey" />
            <p className="text-16 leading-[1.7] text-neutral-black/50">
              The message was removed after 6 months, as part of our data retention.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
